import { apiClient } from "@/lib/api";
import type { Changed } from "@/components/table/changedColumns";
import type { Carrier } from "@/features/warehouseOrders/warehouseOrders.api";

// The tab Portaal on a relation (docs/partner-portal.md, docs/auth.md §3): the people of a customer who
// sign in on the customer portal. partner_users; the API enforces it.

export type PartnerLanguage = "nl" | "en" | "de";
export const PARTNER_LANGUAGES: PartnerLanguage[] = ["nl", "en", "de"];

export interface PartnerUser extends Changed {
  id: string;
  email: string;
  name: string;
  language: PartnerLanguage;
  /** invited: not set up yet; active: set up; disabled: turned off. */
  status: "invited" | "active" | "disabled";
  lockedUntil: string | null;
  lastLoginAt: string | null;
  invitedAt: string | null;
  invitedBy: { id: string; name: string | null } | null;
  /** When the open invitation stops working; null when there is none (or it is set up). */
  invitationExpiresAt: string | null;
  recoveryCodesLeft: number | null;
  /** Every relation they work for, this one too. */
  parties: { id: string; code: string; name: string }[];
  createdAt: string;
}

/**
 * Voorraad in het portaal (docs/partner-portal.md §16 step 7): only a customer can have it; it shows the stock of the
 * warehouse owners in `owners` (the relation's WMS identifiers), and nothing while there are none.
 */
export interface PortalStock {
  customer: boolean;
  enabled: boolean;
  owners: string[];
}

export interface PartnerUsers {
  users: PartnerUser[];
  /** Whether invitations go by mail; without, only a local API shows the link here. */
  mail: { configured: boolean; linksShown: boolean };
  stock: PortalStock;
}

/** What an invitation did: mailed, until when, and — locally without mail — the link. */
export interface Invitation {
  mailed: boolean;
  expiresAt: string;
  link?: string;
}

export const partnerUserKeys = {
  byParty: (partyId: string) => ["partner-users", partyId] as const,
};

export function listPartnerUsers(partyId: string): Promise<PartnerUsers> {
  return apiClient.get<PartnerUsers>(`/api/v1/parties/${partyId}/partner-users`);
}

/** A new address: the account and the invitation. One that is on the portal already: linked here too. */
export function invitePartnerUser(
  partyId: string,
  input: { email: string; displayName: string; language: PartnerLanguage },
): Promise<{ user: PartnerUser; invitation?: Invitation; linked?: boolean }> {
  return apiClient.post(`/api/v1/parties/${partyId}/partner-users`, input);
}

export function setPortalStock(partyId: string, stock: boolean): Promise<{ stock: PortalStock }> {
  return apiClient.patch(`/api/v1/parties/${partyId}/partner-portal`, { stock });
}

/** A carrier of the customer's own in the portal (docs/partner-portal.md §19): a name, counted as a fixed carrier. */
export interface PortalCarrier extends Changed {
  id: string;
  name: string;
  carrier: Carrier;
}

export const portalCarrierKeys = {
  byParty: (partyId: string) => ["portal-carriers", partyId] as const,
};

export function listPortalCarriers(partyId: string): Promise<{ carriers: PortalCarrier[] }> {
  return apiClient.get(`/api/v1/parties/${partyId}/portal-carriers`);
}

/** The list as it should be: one with an id keeps it, one without is new, one left out goes. */
export function savePortalCarriers(partyId: string, carriers: { id?: string; name: string; carrier: Carrier }[]): Promise<{ carriers: PortalCarrier[] }> {
  return apiClient.put(`/api/v1/parties/${partyId}/portal-carriers`, { carriers });
}

export function unlinkPartnerUser(partyId: string, id: string): Promise<void> {
  return apiClient.delete(`/api/v1/parties/${partyId}/partner-users/${id}`);
}

export function reinvitePartnerUser(id: string): Promise<{ user: PartnerUser; invitation: Invitation }> {
  return apiClient.post(`/api/v1/partner-users/${id}/invite`, {});
}

export function resetPartnerUser(id: string): Promise<{ user: PartnerUser; invitation: Invitation }> {
  return apiClient.post(`/api/v1/partner-users/${id}/reset`, {});
}

export function unlockPartnerUser(id: string): Promise<{ user: PartnerUser }> {
  return apiClient.post(`/api/v1/partner-users/${id}/unlock`, {});
}

export function updatePartnerUser(
  id: string,
  input: { displayName?: string; language?: PartnerLanguage; isActive?: boolean },
): Promise<{ user: PartnerUser }> {
  return apiClient.patch(`/api/v1/partner-users/${id}`, input);
}

export type BatchOutcome = "invited" | "linked" | "exists" | "failed";

export interface BatchResult {
  results: {
    email: string;
    outcome: BatchOutcome;
    /** With failed: invalid_email, invalid_name, duplicate or error. */
    reason?: string;
    user?: PartnerUser;
    invitation?: Invitation;
  }[];
  counts: Record<BatchOutcome, number>;
}

/** The people of a customer at once (docs/partner-portal.md §15): each line as one invitation, told per line. */
export function invitePartnerUsers(
  partyId: string,
  people: { email: string; displayName: string; language: PartnerLanguage }[],
): Promise<BatchResult> {
  return apiClient.post(`/api/v1/parties/${partyId}/partner-users/batch`, { people });
}

export interface KnownPartnerUser {
  email: string;
  name: string;
  status: PartnerUser["status"];
  onThisRelation: boolean;
  parties: { id: string; code: string; name: string }[];
}

/** Which of these addresses are on the portal already. A POST: the addresses stay out of the URL. */
export function lookupPartnerUsers(partyId: string, emails: string[]): Promise<{ people: KnownPartnerUser[] }> {
  return apiClient.post(`/api/v1/parties/${partyId}/partner-users/lookup`, { emails });
}
